/**
 * Shared Table Styling Configuration
 * Location: shared/theme/tableStyles.ts
 *
 * SINGLE SOURCE OF TRUTH for all table styling across the app.
 * Import from here to ensure consistent look and feel everywhere.
 */

import { SxProps, Theme } from '@mui/material/styles';

// ============================================================================
// COLOR PALETTES
// ============================================================================

/**
 * Avatar/Chip colors - all dark colors that work well with white text
 * Use getColorFromString() to get a consistent color based on any string
 */
export const AVATAR_COLORS = [
  '#1565c0', '#2e7d32', '#6a1b9a', '#0d47a1',
  '#00695c', '#33691e', '#e65100', '#4a148c', '#004d40',
  '#880e4f', '#311b92', '#1a237e', '#01579b', '#004d40',
] as const;

/**
 * Status colors for chips — light / dark pairs
 */
export const STATUS_COLORS_BY_MODE = {
  success: { light: '#2e7d32', dark: '#66bb6a' },
  warning: { light: '#ef6c00', dark: '#ffa726' },
  error:   { light: '#c62828', dark: '#ef5350' },
  info:    { light: '#1565c0', dark: '#42a5f5' },
  default: { light: '#616161', dark: '#bdbdbd' },
} as const;

/** Legacy accessor — returns light-mode color for backward compatibility */
export const STATUS_COLORS = {
  success: STATUS_COLORS_BY_MODE.success.light,
  warning: STATUS_COLORS_BY_MODE.warning.light,
  error: STATUS_COLORS_BY_MODE.error.light,
  info: STATUS_COLORS_BY_MODE.info.light,
  default: STATUS_COLORS_BY_MODE.default.light,
} as const;

/** Get the right status color for the current theme mode */
export const getStatusColorForMode = (
  key: keyof typeof STATUS_COLORS_BY_MODE,
  mode: 'light' | 'dark' = 'light',
): string => STATUS_COLORS_BY_MODE[key][mode];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Generate a consistent color based on any string.
 * Same string will always return the same color.
 */
export const getColorFromString = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

/**
 * Format snake_case or kebab-case to Title Case
 * e.g., "hello_world" -> "Hello World"
 */
export const formatToTitleCase = (str: string): string => {
  if (!str) return '';
  return str
    .split(/[_-]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Light-mode counterpart for a dark hex color (used for dark-mode text & border).
 * Maps common dark colors to their lighter MUI equivalents.
 */
const LIGHT_COLOR_MAP: Record<string, string> = {
  '#2e7d32': '#66bb6a',
  '#ef6c00': '#ffa726',
  '#c62828': '#ef5350',
  '#1565c0': '#42a5f5',
  '#616161': '#bdbdbd',
  '#7b1fa2': '#ba68c8',
  '#00838f': '#4dd0e1',
  '#388e3c': '#66bb6a',
  '#558b2f': '#9ccc65',
  '#6a1b9a': '#ab47bc',
  '#00695c': '#4db6ac',
  '#4527a0': '#7e57c2',
  '#283593': '#5c6bc0',
  '#0d47a1': '#42a5f5',
  '#33691e': '#9ccc65',
  '#e65100': '#ff9800',
  '#4a148c': '#ab47bc',
  '#004d40': '#4db6ac',
  '#880e4f': '#ec407a',
  '#311b92': '#7e57c2',
  '#1a237e': '#5c6bc0',
  '#01579b': '#29b6f6',
};

const lightenColor = (hex: string): string => LIGHT_COLOR_MAP[hex.toLowerCase()] || hex;

/**
 * Get chip sx props for a colored chip with transparent background.
 * Automatically adapts to dark mode via MUI theme callbacks —
 * no changes needed at call sites.
 *
 * @param color   - The base color (hex, tuned for light mode)
 * @param opacity - Background opacity suffix (hex, default '20' ≈ 12.5%)
 */
export const getChipSx = (color: string, opacity: string = '20') => ({
  bgcolor: (theme: Theme) => {
    const c = theme.palette.mode === 'dark' ? lightenColor(color) : color;
    const op = theme.palette.mode === 'dark' ? '30' : opacity;
    return `${c}${op}`;
  },
  color: (theme: Theme) =>
    theme.palette.mode === 'dark' ? lightenColor(color) : color,
  fontWeight: 500,
  borderRadius: '8px',
});

// ============================================================================
// TABLE COLUMN WIDTH PRESETS
// ============================================================================

export const TABLE_COLUMN_WIDTHS = {
  // Avatar/Small content columns
  ICON: '5%',
  AVATAR: '8%',

  // Small text columns
  SMALL: '10%',

  // Standard text columns
  MEDIUM: '15%',
  LARGE: '20%',
  EXTRA_LARGE: '25%',

  // Action columns
  ACTIONS_SMALL: '10%',
  ACTIONS_MEDIUM: '12%',
  ACTIONS_LARGE: '15%',

  // Custom presets for common layouts
  USER_NAME: '18%',
  USER_EMAIL: '20%',
  STATUS: '10%',
  ROLE: '13%',
  COMPANY: '15%',
  VERIFICATION: '12%',
  PERMISSIONS: '10%',
  CERTIFICATIONS: '12%',
  ACTIONS: '10%',
} as const;

// ============================================================================
// TABLE SPACING AND LAYOUT
// ============================================================================

// Numeric spacing values (in pixels) - use for calculations and math operations
export const TABLE_SPACING_VALUES = {
  // Padding (numeric)
  CELL_PADDING: 12,
  ROW_PADDING_X: 16,
  ROW_PADDING_Y: 8,

  // Gap between elements (numeric)
  GAP_SMALL: 8,
  GAP_MEDIUM: 12,
  GAP_LARGE: 16,

  // Row heights (numeric)
  ROW_HEIGHT_COMPACT: 48,
  ROW_HEIGHT_NORMAL: 56,
  ROW_HEIGHT_COMFORTABLE: 64,
} as const;

// String spacing values (in pixels) - use in sx props and CSS
export const TABLE_SPACING = {
  // Padding
  CELL_PADDING: '12px',
  ROW_PADDING_X: '16px',
  ROW_PADDING_Y: '8px',

  // Gap between elements
  GAP_SMALL: '8px',
  GAP_MEDIUM: '12px',
  GAP_LARGE: '16px',

  // Row heights
  ROW_HEIGHT_COMPACT: '48px',
  ROW_HEIGHT_NORMAL: '56px',
  ROW_HEIGHT_COMFORTABLE: '64px',
} as const;

// ============================================================================
// TABLE CONTAINER STYLES
// ============================================================================

export const tableContainerSx: SxProps<Theme> = {
  boxShadow: 1,
  borderRadius: 3,
  overflow: 'hidden',
};

// ============================================================================
// TABLE HEADER STYLES
// ============================================================================

/**
 * Header row background - theme aware
 */
export const getHeaderRowBgColor = (theme: Theme): string => {
  return theme.palette.mode === 'dark' ? '#424242' : '#e0e0e0'; // grey.800 : grey.200
};

export const tableHeaderRowSx: SxProps<Theme> = {
  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
};

export const tableHeaderCellSx: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: '0.875rem',
};

// Legacy - kept for backward compatibility but prefer the above
export const tableHeaderSx: SxProps<Theme> = {
  backgroundColor: 'background.paper',
  borderBottom: '2px solid',
  borderColor: 'divider',
  fontWeight: 600,
  textTransform: 'uppercase',
  fontSize: '0.75rem',
  letterSpacing: '0.5px',
  color: 'text.primary',
  padding: `${TABLE_SPACING.ROW_PADDING_Y} ${TABLE_SPACING.ROW_PADDING_X}`,

  '& > th': {
    padding: `${TABLE_SPACING.ROW_PADDING_Y} ${TABLE_SPACING.ROW_PADDING_X}`,
    fontWeight: 700,
  },
};

// ============================================================================
// TABLE ROW STYLES
// ============================================================================

/**
 * Get striped row background color - theme aware
 */
export const getStripedRowBgColor = (theme: Theme): string => {
  return theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fafafa'; // grey.50
};

export const tableRowSx: SxProps<Theme> = {
  '&:hover': {
    backgroundColor: 'action.hover',
  },
  borderBottom: '1px solid',
  borderColor: 'divider',
  padding: 0,

  '& > td': {
    padding: TABLE_SPACING.CELL_PADDING,
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
};

// ============================================================================
// TABLE CELL STYLES
// ============================================================================

export const tableBodyCellSx: SxProps<Theme> = {
  padding: TABLE_SPACING.CELL_PADDING,
  fontSize: '0.875rem',
  color: 'text.primary',
  fontWeight: 500,

  '&:first-of-type': {
    paddingLeft: TABLE_SPACING.ROW_PADDING_X,
  },

  '&:last-of-type': {
    paddingRight: TABLE_SPACING.ROW_PADDING_X,
  },
};

// ============================================================================
// PAGINATION STYLES
// ============================================================================

export const paginationSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: TABLE_SPACING.CELL_PADDING,
  borderTop: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.paper',

  '& .MuiTablePagination-root': {
    borderTop: 'none',
  },
};

// ============================================================================
// CHIP STYLES (used in status, roles, categories, etc)
// ============================================================================

export const STATUS_CHIP_STYLES = {
  ACTIVE: {
    label: 'Activo',
    color: STATUS_COLORS.success,
  },
  INACTIVE: {
    label: 'Inactivo',
    color: STATUS_COLORS.default,
  },
  VERIFIED: {
    label: 'Verificado',
    color: STATUS_COLORS.success,
  },
  PENDING: {
    label: 'Pendiente',
    color: STATUS_COLORS.warning,
  },
  REJECTED: {
    label: 'Rechazado',
    color: STATUS_COLORS.error,
  },
  PUBLISHED: {
    label: 'Publicado',
    color: STATUS_COLORS.success,
  },
  DRAFT: {
    label: 'Borrador',
    color: STATUS_COLORS.warning,
  },
} as const;

// ============================================================================
// ROLE BADGE STYLES
// ============================================================================

export const ROLE_BADGE_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'> = {
  super_admin: 'error',
  safety_staff: 'warning',
  company_admin: 'primary',
  client_approver: 'info',
  client_staff: 'default',
  security: 'secondary',
} as const;

/**
 * Role-specific hex colors for inline chip styling
 */
export const ROLE_COLORS: Record<string, string> = {
  super_admin: '#7b1fa2',
  safety_staff: '#1565c0',
  company_admin: '#388e3c',
  client_approver: '#ef6c00',
  client_staff: '#558b2f',
  security: '#6a1b9a',
} as const;

// ============================================================================
// AVATAR STYLES
// ============================================================================

export const getAvatarSx = (size: 'small' | 'medium' | 'large' = 'medium'): SxProps<Theme> => {
  const sizes = {
    small: { width: 32, height: 32, fontSize: '0.75rem' },
    medium: { width: 36, height: 36, fontSize: '0.875rem' },
    large: { width: 40, height: 40, fontSize: '1rem' },
  };

  return {
    ...sizes[size],
    fontWeight: 600,
    border: '2px solid',
    borderColor: (theme) => theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.2)'
      : 'rgba(0,0,0,0.1)',
    color: '#fff',
  };
};

export const avatarSx: SxProps<Theme> = {
  width: 32,
  height: 32,
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: (theme) => (theme.palette as any).background?.primary || theme.palette.primary.main,
  color: 'primary.contrastText',
};

// ============================================================================
// USER NAME/INFO BOX STYLES
// ============================================================================

export const userInfoBoxSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: TABLE_SPACING.GAP_SMALL,
  cursor: 'pointer',

  '&:hover': {
    '& .userName': {
      textDecoration: 'underline',
    },
  },
};

export const userNameSx: SxProps<Theme> = {
  fontWeight: 500,
  color: (theme) => (theme.palette as any).background?.primary || theme.palette.primary.main,
  fontSize: '0.875rem',
};

export const userEmailSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  color: 'text.secondary',
  fontWeight: 400,
};

// ============================================================================
// ACTION MENU STYLES
// ============================================================================

export const actionMenuButtonSx: SxProps<Theme> = {
  color: 'text.secondary',

  '&:hover': {
    backgroundColor: 'action.hover',
  },
};

export const actionMenuSx: SxProps<Theme> = {
  '& .MuiMenu-paper': {
    minWidth: 200,
    boxShadow: 2,
  },
};

// ============================================================================
// SEARCH STYLES
// ============================================================================

export const collapsibleSearchSx: SxProps<Theme> = {
  width: { xs: 180, sm: 220 },
  mr: 1,
  '& .MuiOutlinedInput-root': {
    borderRadius: '20px',
  },
};

export const getSearchButtonSx = (isActive: boolean): SxProps<Theme> => ({
  bgcolor: (theme) => isActive
    ? theme.palette.primary.main
    : theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
  color: (theme) => isActive ? (theme.palette.background.buttonText || theme.palette.primary.contrastText) : 'inherit',
  '&:hover': {
    bgcolor: (theme) => isActive
      ? theme.palette.primary.dark
      : theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300',
  },
  transition: 'all 0.3s ease',
});

export const searchFieldSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'background.paper',

    '&:hover fieldset': {
      borderColor: (theme) => (theme.palette as any).background?.primary || theme.palette.primary.main,
    },
  },
};

export const filterChipSx: SxProps<Theme> = {
  height: 32,
  borderRadius: '16px',
};

// ============================================================================
// BUTTON STYLES
// ============================================================================

export const primaryButtonSx: SxProps<Theme> = {
  bgcolor: (theme) => theme.palette.background.buttonBg || theme.palette.primary.main,
  color: (theme) => theme.palette.background.buttonText || theme.palette.primary.contrastText,
  // Inherit the platform default radius (shape.borderRadius = 8) instead of a
  // one-off pill; buttons share one shape app-wide.
  '&:hover': {
    bgcolor: (theme) => theme.palette.background.buttonBg || theme.palette.primary.main,
    filter: 'brightness(0.9)',
  },
};

// Icon-only by default, expands to reveal its label on hover/focus — for a
// header row with several action buttons that would otherwise need to
// wrap (Users.tsx's Nuevo Usuario/Agregar Proveedor/swap row; the
// verifications pages' Nueva Verificación/Descargar PDF/Enviar
// Masiva/switch-page row). Usage: icon as a plain child (NOT startIcon,
// so it stays visible while the label collapses), label wrapped in
// `<Box component="span" className="btn-expand-label">`. The label needs
// max-width/opacity transitions, not `width: auto` — CSS can't animate to
// auto. When merging with another sx object typed `SxProps<Theme>`, use
// `sx={[otherSx as any, expandingButtonSx]}` — a plain object spread can
// trip TS.
export const expandingButtonSx = {
  minWidth: 0,
  '& .btn-expand-label': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    maxWidth: 0,
    opacity: 0,
    marginLeft: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    transition: 'max-width 0.6s ease, opacity 0.5s ease, margin-left 0.6s ease',
  },
  '&:hover .btn-expand-label, &:focus-visible .btn-expand-label': {
    maxWidth: 280,
    opacity: 1,
    marginLeft: '8px',
  },
} as const;

// ============================================================================
// TOOLBAR/HEADER STYLES
// ============================================================================

export const tableToolbarSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: TABLE_SPACING.GAP_LARGE,
  flexWrap: 'wrap',
  gap: TABLE_SPACING.GAP_MEDIUM,
  padding: TABLE_SPACING.CELL_PADDING,
};

export const tableButtonGroupSx: SxProps<Theme> = {
  display: 'flex',
  gap: TABLE_SPACING.GAP_SMALL,
  flexWrap: 'wrap',
};

// ============================================================================
// EMPTY STATE STYLES
// ============================================================================

export const emptyStateSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: `${TABLE_SPACING_VALUES.CELL_PADDING * 2}px`,
  minHeight: 400,
  backgroundColor: 'background.paper',
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
};

// ============================================================================
// TOOLTIP STYLES
// ============================================================================

export const tooltipSx: SxProps<Theme> = {
  '& .MuiTooltip-tooltip': {
    backgroundColor: 'background.paper',
    color: 'text.primary',
    boxShadow: 2,
    border: '1px solid',
    borderColor: 'divider',
    padding: TABLE_SPACING.CELL_PADDING,
    fontSize: '0.75rem',
  },
};

// ============================================================================
// RESPONSIVE ADJUSTMENTS
// ============================================================================

export const getResponsiveTableSx = (): SxProps<Theme> => ({
  // Desktop
  '@media (min-width: 1280px)': {
    '& .MuiTableCell-root': {
      padding: TABLE_SPACING.CELL_PADDING,
    },
  },

  // Tablet
  '@media (max-width: 1279px) and (min-width: 600px)': {
    '& .MuiTableCell-root': {
      padding: '10px 8px',
      fontSize: '0.8125rem',
    },
  },

  // Mobile
  '@media (max-width: 599px)': {
    '& .MuiTableCell-root': {
      padding: '8px 4px',
      fontSize: '0.75rem',
    },
  },
});

// ============================================================================
// LAYOUT PRESETS (for different table types)
// ============================================================================

export const TABLE_LAYOUTS = {
  // Simple list (name, status, actions)
  SIMPLE: {
    columns: {
      name: TABLE_COLUMN_WIDTHS.LARGE,
      status: TABLE_COLUMN_WIDTHS.MEDIUM,
      actions: TABLE_COLUMN_WIDTHS.ACTIONS_SMALL,
    },
  },

  // Standard CRUD (name, role, status, actions)
  STANDARD: {
    columns: {
      name: TABLE_COLUMN_WIDTHS.LARGE,
      role: TABLE_COLUMN_WIDTHS.MEDIUM,
      status: TABLE_COLUMN_WIDTHS.SMALL,
      actions: TABLE_COLUMN_WIDTHS.ACTIONS_MEDIUM,
    },
  },

  // Users (name, role, verification, permissions, certifications, company, status, actions)
  USERS: {
    columns: {
      name: TABLE_COLUMN_WIDTHS.USER_NAME,
      role: TABLE_COLUMN_WIDTHS.ROLE,
      verified: TABLE_COLUMN_WIDTHS.VERIFICATION,
      permits: TABLE_COLUMN_WIDTHS.PERMISSIONS,
      certs: TABLE_COLUMN_WIDTHS.CERTIFICATIONS,
      company: TABLE_COLUMN_WIDTHS.COMPANY,
      status: TABLE_COLUMN_WIDTHS.STATUS,
      actions: TABLE_COLUMN_WIDTHS.ACTIONS,
    },
  },

  // Departments (name, company, members, status, actions)
  DEPARTMENTS: {
    columns: {
      name: TABLE_COLUMN_WIDTHS.LARGE,
      company: TABLE_COLUMN_WIDTHS.MEDIUM,
      members: TABLE_COLUMN_WIDTHS.SMALL,
      status: TABLE_COLUMN_WIDTHS.SMALL,
      actions: TABLE_COLUMN_WIDTHS.ACTIONS_MEDIUM,
    },
  },

  // Templates (name, category, status, actions)
  TEMPLATES: {
    columns: {
      name: '40%',
      category: '20%',
      status: '20%',
      actions: '10%',
    },
  },

  // Companies (name, industry, permits, actions)
  COMPANIES: {
    columns: {
      name: '40%',
      industry: '25%',
      permits: '20%',
      actions: '10%',
    },
  },
} as const;

// ============================================================================
// LEGACY UTILITY FUNCTIONS (for backward compatibility)
// ============================================================================

/**
 * Get column width from preset or custom value
 */
export const getColumnWidth = (width: keyof typeof TABLE_COLUMN_WIDTHS | string): string => {
  if (width in TABLE_COLUMN_WIDTHS) {
    return TABLE_COLUMN_WIDTHS[width as keyof typeof TABLE_COLUMN_WIDTHS];
  }
  return width;
};

/**
 * Get role badge color
 */
export const getRoleBadgeColor = (role: string): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
  return ROLE_BADGE_COLORS[role] || 'default';
};

/**
 * Get status chip color
 */
export const getStatusColor = (status: 'active' | 'inactive' | 'verified' | 'pending' | 'rejected'): any => {
  return STATUS_CHIP_STYLES[status.toUpperCase() as keyof typeof STATUS_CHIP_STYLES];
};
